/**
 * Import Notes Modal
 * -------------------
 * This modal allows users to upload a CSV/XLS file containing notes data,
 * map the uploaded columns to expected fields (Note, Contact ID, Created At),
 * preview the parsed rows, deselect unwanted rows, and finally import them.
 *
 * It handles:
 *  - File upload and parsing
 *  - Dynamic column mapping
 *  - Row selection/deselection
 *  - Data validation
 *  - Submission with mapping information
 */

import Modal from "react-modal";
import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import { Checkbox } from "@material-ui/core";
import { IoIosClose, IoMdDocument } from "react-icons/io";
import { handleFileUpload } from "../../../Services/contacts";
import { useDispatch } from "react-redux";
import Loading from "../../Loading/Loading";
import { mapNotes } from "../../../Services/resources";

type props = {
  open: boolean;
  close: () => void;
};

const ImportNotesModal: FunctionComponent<props> = ({ open, close }) => {
  /** Columns extracted from uploaded file */
  const [columns, setColumns] = useState([]);

  /** Parsed row data from uploaded sheet */
  const [data, setData] = useState([]);

  /** Row indices that the user has opted to exclude */
  const [select, setSelected] = useState<any[]>([]);

  /** Loading state for upload + mapping process */
  const [load, setLoad] = useState(false);

  /**
   * Column mapping object:
   *  {
   *    "Uploaded Column Name": "Mapped Field Name"
   *  }
   */
  const [colName, setColName] = useState<{ [key: string]: string }>({});

  /**
   * Allowed mapping choices for uploaded columns.
   * Prevents duplicate mapping by disabling already-selected ones.
   */
  const [choice, setChoice] = useState([
    { name: "Note", selected: false },
    { name: "Contact ID", selected: false },
    { name: "Created At", selected: false },
  ]);

  const dispatcher = useDispatch();

  /**
   * Update "selected" flag on each choice option when a mapping changes.
   * Ensures a field cannot be mapped twice.
   */
  useEffect(() => {
    const columns = Object.values(colName);
    let tempChoices = [...choice];
    tempChoices.forEach((choice, index) => {
      if (columns.includes(choice.name)) {
        tempChoices[index].selected = true;
      } else {
        tempChoices[index].selected = false;
      }
    });

    setChoice(tempChoices);
    // eslint-disable-next-line
  }, [colName]);

  /**
   * When new columns are detected from uploaded file,
   * initialize column mapping structure.
   */
  useEffect(() => {
    let data: {} = {};
    columns.forEach((item: any, index: number) => {
      data = {
        ...data,
        [item.name]: "",
      };
    });
    setColName(data);
  }, [columns]);

  /**
   * Reads and parses uploaded CSV/XLS file,
   * extracting columns + rows.
   */
  const upload = (file: any) => {
    handleFileUpload(
      file,
      (parsedData: any) => setData(parsedData),
      (extractedColumns: any) => setColumns(extractedColumns),
      (loading: boolean) => setLoad(loading)
    );
  };

  /**
   * Final submission:
   * 1. Create mapping object
   * 2. Remove deselected rows
   * 3. Pass cleaned data + mapping to backend
   */
  const onSubmit = () => {
    setLoad(true);

    let mapping: { [key: string]: string } = {};

    // Build mapping only for fields actually selected
    Object.keys(colName).forEach((key) => {
      if (colName[key] !== "") mapping[colName[key]] = key;
    });

    // Duplicate array to filter rows safely
    let cleanedRows: any[] = [...data];

    // Sort & remove deselected rows in reverse order
    select.sort((a, b) => b - a);
    select.forEach((index) => cleanedRows.splice(index, 1));

    // Send to backend
    mapNotes(cleanedRows, mapping, dispatcher, close, (state) => setLoad(state));
  };

  /**
   * Handles selecting a mapped field from dropdown.
   */
  const onSelect = (item: any, value: string) => {
    let data: any = { ...colName };

    for (var key in data) {
      if (key === item.name) {
        data[key] = value === "Select" ? "" : value;
      }
    }
    setColName(data);
  };

  return (
    <Modal
      style={{ overlay: { zIndex: 100 } }}
      className={styles.parent}
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {load && <Loading />}

      {/* IF DATA IS PARSED → SHOW TABLE + MAPPING UI */}
      {data.length !== 0 ? (
        <>
          {/* Close Button */}
          <div className={styles.cross} onClick={close}>
            <IoIosClose size={35} color={"#808080"} />
          </div>

          <p className={styles.contactForm}>Import Data for "Notes Form"</p>

          {/* Data Preview Table */}
          <div className={styles.tableBox}>
            <table className={styles.table}>
              <thead className={styles.head}>
                <tr>
                  <th className={styles.th}></th>
                  <th className={styles.th}>Sno.</th>

                  {/* Each uploaded column */}
                  {columns.map((item: any, index: number) => (
                    <th key={index} className={styles.th}>
                      <div className={styles.tHeadContainer}>
                        <p>{item.name}</p>
                      </div>

                      {/* Column mapping dropdown */}
                      <div className={styles.drop}>
                        <select
                          key={index}
                          className={styles.options}
                          onChange={(e) => onSelect(item, e.target.value)}
                        >
                          <option>Select</option>
                          {choice.map((choiceItem: any, idx: number) => (
                            <option disabled={choiceItem.selected} key={idx}>
                              {choiceItem.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </th>
                  ))}

                  <th className={styles.th}></th>
                </tr>
              </thead>

              <tbody>
                {/* Each data row */}
                {data.map((row: any, index: number) => (
                  <tr
                    key={index}
                    className={styles.no}
                    style={{
                      backgroundColor: select.includes(index)
                        ? "#00000030"
                        : "#ffffff",
                      borderBottomColor: "#f3f3f3",
                      borderBottomWidth: "5px",
                    }}
                  >
                    {/* Enable/Disable row checkbox */}
                    <td>
                      <Checkbox
                        key={index}
                        checked={select.includes(index) ? false : true}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            let data = [...select];
                            data.push(index);
                            setSelected(data);
                          } else if (e.target.checked) {
                            let data = [...select];
                            let item = select.indexOf(index);
                            if (item > -1) {
                              data.splice(item, 1);
                            }
                            setSelected(data);
                          }
                        }}
                      />
                    </td>

                    <td className={styles.td}>{index + 1}</td>

                    {/* Cell values */}
                    {Object.values(row).map((colValue: any, idx: number) => (
                      <td className={styles.td} key={idx}>
                        {colValue}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Buttons */}
          <div className={styles.buttonBox}>
            <button className={styles.cancel} onClick={close}>
              Cancel
            </button>
            <button className={styles.confirm} onClick={onSubmit}>
              Confirm
            </button>
          </div>
        </>
      ) : (
        /* IF NO FILE UPLOADED → SHOW DRAG-AND-DROP UI */
        <div className={styles.child}>
          <div className={styles.cross} onClick={close}>
            <IoIosClose size={35} color={"#808080"} />
          </div>

          <p className={styles.contactForm}>Import Data for "Notes Form"</p>

          {/* Drag & Drop File Upload */}
          <div
            className={styles.dragBox}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              upload(e.dataTransfer.files[0]);
            }}
          >
            <IoMdDocument size={100} color={"#808080"} />
            <p>Drag Your Files Here</p>
          </div>

          {/* Upload Button */}
          <div className={styles.selectBox}>
            <label htmlFor="file-input" className={styles.uploadButton}>
              Upload File
            </label>

            <input
              id="file-input"
              className={styles.select}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e: any) => upload(e.target.files[0])}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ImportNotesModal;
